import React from "react";
import { Link } from "react-router-dom";
import { PlusIcon as Plus, HeartIcon as Heart, StorefrontIcon as Storefront, ChartBarIcon as ChartBar, ShareIcon as Share, ArrowSquareOutIcon as ArrowSquareOut, MegaphoneIcon as Megaphone } from "@phosphor-icons/react";

/**
 * Owner-only quick-action strip on the kennel detail page.
 *
 * Every action is a single tap into a high-value flow the breeder
 * does often: list a new dog, plan a breeding, jump to the
 * storefront / analytics, preview the public profile, share it.
 *
 * Each button is intentionally large + has a verb-first label so the
 * page doubles as a launchpad. Buyers viewing someone else's kennel
 * never see this strip (parent component gates on `editable`).
 */
export default function KennelQuickActions({ breeder, publicUrl, onShare }) {
  const slugOrId = breeder?.slug || breeder?.id;
  const actions = [
    {
      to: "/dogs/new",
      label: "Add a dog",
      icon: Plus,
      tone: "ink",
      testid: "kennel-quick-add-dog",
    },
    {
      to: "/litters/upcoming/new",
      label: "Plan a breeding",
      icon: Heart,
      tone: "crimson",
      testid: "kennel-quick-plan-breeding",
    },
    {
      to: "/me/store",
      label: "My storefront",
      icon: Storefront,
      tone: "ink",
      testid: "kennel-quick-storefront",
    },
    {
      to: "/breeder/analytics",
      label: "Analytics",
      icon: ChartBar,
      tone: "ink",
      testid: "kennel-quick-analytics",
    },
    {
      to: "/share-kennel",
      label: "Promote",
      icon: Megaphone,
      tone: "gold",
      testid: "kennel-quick-promote",
    },
  ];

  return (
    <div
      className="bg-white border border-[#EAEAEA] rounded-xl p-4 md:p-5 mb-6 shadow-luxury-light"
      data-testid="kennel-quick-actions"
    >
      <div className="flex flex-wrap items-center gap-2">
        {actions.map((a) => {
          const Icon = a.icon;
          const colorClass =
            a.tone === "crimson"
              ? "border-[#E5C8C8] text-[#7A1A1A] hover:bg-[#FFF0F0] hover:border-[#C1272D]/40"
              : a.tone === "gold"
                ? "border-[#E2DAC2] text-[#8A6A00] hover:bg-cream hover:border-gold/60"
                : "border-[#E5E5E5] text-ink hover:bg-[#FAFAFA] hover:border-ink/30";
          return (
            <Link
              key={a.to}
              to={a.to}
              className={`inline-flex items-center gap-1.5 rounded-full border h-10 px-4 text-[12.5px] font-semibold transition-colors ${colorClass}`}
              data-testid={a.testid}
            >
              <Icon size={14} weight="duotone" />
              {a.label}
            </Link>
          );
        })}
        <a
          href={publicUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] text-ink hover:bg-[#FAFAFA] hover:border-ink/30 h-10 px-4 text-[12.5px] font-semibold transition-colors"
          data-testid="kennel-quick-preview"
          title={publicUrl}
        >
          <ArrowSquareOut size={14} weight="duotone" />
          <span className="hidden sm:inline">Preview public page</span>
          <span className="sm:hidden">Preview</span>
        </a>
        <button
          type="button"
          onClick={onShare}
          className="inline-flex items-center gap-1.5 rounded-full bg-ink hover:bg-crimson text-white h-10 px-4 sm:px-5 text-[12.5px] font-semibold sm:ml-auto transition-colors min-w-0"
          data-testid="kennel-quick-share"
        >
          <Share size={14} weight="duotone" />
          <span className="hidden sm:inline truncate">Share /k/{slugOrId?.slice(0, 16)}{slugOrId && slugOrId.length > 16 ? "…" : ""}</span>
          <span className="sm:hidden">Share</span>
        </button>
      </div>
    </div>
  );
}
